import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Tooltip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  DialogContentText
} from '@mui/material';
import {
  School as SchoolIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Add as AddIcon,
  Topic as TopicIcon,
  Save as SaveIcon,
  Business as BusinessIcon,
  Store as StoreIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import workOrderDetailService from '../../services/workOrderDetailService';

const MATRIZ_VALUE = 'MATRIZ';

const Capacitaciones = () => {
  const [trainings, setTrainings] = useState([]);
  const [workOrdersMap, setWorkOrdersMap] = useState({});
  const [companiesMap, setCompaniesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Confirmación previa a "Generar Constancias" -- mismo criterio que Programas.jsx: mostrar
  // qué se va a generar antes de disparar la generación real.
  const [confirmGenerateTraining, setConfirmGenerateTraining] = useState(null);
  const [generatingConstancias, setGeneratingConstancias] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [form, setForm] = useState({
    workOrderId: '', branchId: MATRIZ_VALUE, trainingTopic: '', trainingDate: '',
    startTime: '', endTime: '', tutorName: '', durationHours: '', notes: ''
  });
  const [workOrders, setWorkOrders] = useState([]);
  const [entitiesByWorkOrder, setEntitiesByWorkOrder] = useState({});
  const [dialogEntities, setDialogEntities] = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);

  // Plantillas de tema+duración configuradas para el servicio de la OT elegida (ver
  // ServiceCatalogController /default-trainings) -- solo sugieren valores al crear, no
  // crean nada por sí solas.
  const [defaultTrainingOptions, setDefaultTrainingOptions] = useState([]);

  // Inscripción de participantes por capacitación (programa) -- solo relevante cuando un
  // ente tiene 2+ capacitaciones; con una sola, todo el padrón la recibe automáticamente.
  const [participantsDialogOpen, setParticipantsDialogOpen] = useState(false);
  const [participantsTraining, setParticipantsTraining] = useState(null);
  const [participantsRoster, setParticipantsRoster] = useState([]);
  const [participantsEnrolledIds, setParticipantsEnrolledIds] = useState(new Set());
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participantsSaving, setParticipantsSaving] = useState(false);

  // Indicadores por fila: participantes esperados/inscritos y constancias generadas.
  // { [trainingId]: { participantCount, rosterSize, generatedCount, singleProgram, ready } }
  const [trainingStatusMap, setTrainingStatusMap] = useState({});

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [trainingsRes, ordersRes, companiesRes] = await Promise.all([
        api.get('/trainings'),
        api.get('/work-orders'),
        api.get('/companies')
      ]);
      const orders = ordersRes.data?.content || ordersRes.data || [];
      const ordersMap = {};
      orders.forEach(o => { ordersMap[o.id] = o; });
      setWorkOrdersMap(ordersMap);
      setWorkOrders(orders);
      const companies = companiesRes.data?.content || companiesRes.data || [];
      const cMap = {};
      companies.forEach(c => { cMap[c.id] = c; });
      setCompaniesMap(cMap);
      const trainingsData = trainingsRes.data || [];
      setTrainings(trainingsData);

      // Cargar entes (matriz/sucursales) de cada orden con capacitaciones, para la columna "Ente"
      const workOrderIds = [...new Set(trainingsData.map(t => t.workOrderId))];
      const entityResults = await Promise.all(
        workOrderIds.map(id => workOrderDetailService.getDetailsByWorkOrder(id).catch(() => []))
      );
      const entMap = {};
      workOrderIds.forEach((id, idx) => { entMap[id] = entityResults[idx] || []; });
      setEntitiesByWorkOrder(entMap);

      await fetchTrainingStatuses(trainingsData);
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al cargar capacitaciones', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Indicadores de "Participantes" y "Constancias" por fila -- una llamada por capacitación.
  const fetchTrainingStatuses = async (trainingsData) => {
    const results = await Promise.all(
      trainingsData.map(t => api.get(`/constancias/training/${t.id}/status`).then(r => r.data).catch(() => null))
    );
    const statusMap = {};
    trainingsData.forEach((t, idx) => { statusMap[t.id] = results[idx]; });
    setTrainingStatusMap(prev => ({ ...prev, ...statusMap }));
  };

  const refreshTrainingStatus = async (trainingId) => {
    try {
      const res = await api.get(`/constancias/training/${trainingId}/status`);
      setTrainingStatusMap(prev => ({ ...prev, [trainingId]: res.data }));
    } catch (e) { /* deja el indicador anterior si falla */ }
  };

  const loadEntitiesForWorkOrder = async (workOrderId) => {
    if (!workOrderId) {
      setDialogEntities([]);
      return;
    }
    setEntitiesLoading(true);
    try {
      const details = await workOrderDetailService.getDetailsByWorkOrder(workOrderId);
      setDialogEntities(details || []);
    } catch (e) {
      setDialogEntities([]);
    } finally {
      setEntitiesLoading(false);
    }
  };

  const getEntityOptions = () => {
    if (dialogEntities.length === 0) {
      return [{ value: MATRIZ_VALUE, label: 'Matriz (General)', isMatriz: true }];
    }
    return dialogEntities.map(d => ({
      value: d.branchId != null ? String(d.branchId) : MATRIZ_VALUE,
      label: d.entityName || (d.entityType === 'MATRIZ' ? 'Matriz' : `Sucursal ${d.branchId}`),
      isMatriz: d.entityType === 'MATRIZ'
    }));
  };

  const getEntityName = (training) => {
    const details = entitiesByWorkOrder[training.workOrderId] || [];
    const match = details.find(d => (d.branchId ?? null) === (training.branchId ?? null));
    if (match) return { name: match.entityName || (match.entityType === 'MATRIZ' ? 'Matriz' : 'Sucursal'), isMatriz: match.entityType === 'MATRIZ' };
    return { name: training.branchId != null ? `Sucursal ${training.branchId}` : 'Matriz', isMatriz: training.branchId == null };
  };

  const handleOpenEdit = (training) => {
    setSelectedTraining(training);
    setForm({
      workOrderId: training.workOrderId || '',
      branchId: training.branchId != null ? String(training.branchId) : MATRIZ_VALUE,
      trainingTopic: training.trainingTopic || '',
      trainingDate: training.trainingDate || '',
      startTime: training.startTime || '',
      endTime: training.endTime || '',
      tutorName: training.tutorName || '',
      durationHours: training.durationHours != null ? String(training.durationHours) : '',
      notes: training.notes || ''
    });
    loadEntitiesForWorkOrder(training.workOrderId);
    setDialogOpen(true);
  };

  const handleOpenNew = () => {
    setSelectedTraining(null);
    setForm({
      workOrderId: '', branchId: MATRIZ_VALUE, trainingTopic: '', trainingDate: '',
      startTime: '', endTime: '', tutorName: '', durationHours: '', notes: ''
    });
    setDialogEntities([]);
    setDefaultTrainingOptions([]);
    setDialogOpen(true);
  };

  const fetchDefaultTrainingOptions = async (workOrderId) => {
    if (!workOrderId) { setDefaultTrainingOptions([]); return; }
    try {
      const res = await api.get(`/trainings/work-order/${workOrderId}/default-training-options`);
      setDefaultTrainingOptions(res.data || []);
    } catch (e) {
      setDefaultTrainingOptions([]);
    }
  };

  const handleApplyDefaultTraining = (option) => {
    setForm(p => ({
      ...p,
      trainingTopic: option.trainingTopic,
      durationHours: option.durationHours != null ? String(option.durationHours) : p.durationHours
    }));
  };

  const handleSave = async () => {
    // Mismas reglas que el backend (TrainingService.validate): todo obligatorio y fin después del inicio
    if (!form.workOrderId || !form.trainingTopic.trim() || !form.trainingDate || !form.startTime
        || !form.endTime || !form.tutorName.trim()) {
      setSnackbar({ open: true, message: 'Completa los campos obligatorios (*)', severity: 'warning' });
      return;
    }
    if (form.endTime <= form.startTime) {
      setSnackbar({ open: true, message: 'La hora de fin debe ser posterior a la hora de inicio', severity: 'warning' });
      return;
    }
    setDialogLoading(true);
    try {
      const payload = {
        workOrderId: parseInt(form.workOrderId),
        branchId: form.branchId === MATRIZ_VALUE ? null : parseInt(form.branchId),
        trainingTopic: form.trainingTopic.trim(),
        trainingDate: form.trainingDate,
        startTime: form.startTime || null,
        endTime: form.endTime || null,
        tutorName: form.tutorName.trim(),
        durationHours: form.durationHours ? parseFloat(form.durationHours) : null,
        notes: form.notes.trim() || null
      };
      if (selectedTraining) {
        await api.put(`/trainings/${selectedTraining.id}`, payload);
      } else {
        await api.post('/trainings', payload);
      }
      setSnackbar({ open: true, message: '✅ Capacitación guardada', severity: 'success' });
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al guardar capacitación', severity: 'error' });
    } finally {
      setDialogLoading(false);
    }
  };

  const handleGenerateConstancias = async (training) => {
    setGeneratingConstancias(true);
    setSnackbar({ open: true, message: 'Generando constancias...', severity: 'info' });
    try {
      await api.post(`/constancias/training/${training.id}/generate-all`);
      setSnackbar({ open: true, message: '✅ Constancias generadas y subidas a Dropbox', severity: 'success' });
      refreshTrainingStatus(training.id);
    } catch (e) {
      const msg = e.response?.data?.message || 'Error al generar constancias';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setGeneratingConstancias(false);
      setConfirmGenerateTraining(null);
    }
  };

  const handleOpenParticipants = async (training) => {
    setParticipantsTraining(training);
    setParticipantsDialogOpen(true);
    setParticipantsRoster([]);
    setParticipantsEnrolledIds(new Set());
    setParticipantsLoading(true);
    try {
      const [rosterRes, enrolledRes] = await Promise.all([
        api.get(`/trainings/${training.id}/enrollment/roster`),
        api.get(`/trainings/${training.id}/enrollment`)
      ]);
      setParticipantsRoster(rosterRes.data || []);
      setParticipantsEnrolledIds(new Set((enrolledRes.data || []).map(m => m.id)));
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al cargar el padrón del ente', severity: 'error' });
    } finally {
      setParticipantsLoading(false);
    }
  };

  const handleToggleParticipant = (uipcId) => {
    setParticipantsEnrolledIds(prev => {
      const next = new Set(prev);
      if (next.has(uipcId)) next.delete(uipcId); else next.add(uipcId);
      return next;
    });
  };

  const handleSaveParticipants = async () => {
    if (!participantsTraining) return;
    setParticipantsSaving(true);
    try {
      await api.post(`/trainings/${participantsTraining.id}/enrollment/sync`, Array.from(participantsEnrolledIds));
      setSnackbar({ open: true, message: '✅ Participantes inscritos actualizados', severity: 'success' });
      setParticipantsDialogOpen(false);
      refreshTrainingStatus(participantsTraining.id);
    } catch (e) {
      const msg = e.response?.data?.error || 'Error al guardar la inscripción';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setParticipantsSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const [y, m, d] = dateStr.split('-');
    const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
  };

  // trainingDate es "YYYY-MM-DD" (LocalDate, sin hora) -- comparar con `new Date(trainingDate)
  // < new Date()` la interpreta como medianoche UTC y en huso horario de México (UTC-6) la
  // capacitación de HOY ya aparecía como "Realizada" desde el inicio del día.
  const isPastTrainingDate = (dateStr) => {
    if (!dateStr) return false;
    const todayLocal = new Date().toLocaleDateString('en-CA');
    return dateStr.substring(0, 10) < todayLocal;
  };

  // Nombre Comercial de la empresa de la orden -- prioriza el businessName del catálogo de
  // Empresas (igual que las demás pantallas); si no está cargado, cae al clientName de la OT.
  const getCompanyName = (t) => {
    const wo = workOrdersMap[t.workOrderId];
    return companiesMap[wo?.companyId]?.businessName || wo?.clientName || '';
  };

  const filtered = trainings.filter(t => {
    const wo = workOrdersMap[t.workOrderId];
    const search = filterText.toLowerCase();
    return (
      (wo?.orderNumber || '').toLowerCase().includes(search) ||
      (getCompanyName(t) || '').toLowerCase().includes(search) ||
      (t.trainingTopic || '').toLowerCase().includes(search) ||
      (t.tutorName || '').toLowerCase().includes(search)
    );
  });

  // Ordenamiento de la tabla (elegido por el usuario: Orden, Nombre Comercial, Tema, Tutor, Duración)
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSortRequest = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortValue = (t, field) => {
    const wo = workOrdersMap[t.workOrderId];
    switch (field) {
      case 'orden': return wo?.orderNumber || '';
      case 'empresa': return getCompanyName(t);
      case 'tema': return t.trainingTopic || '';
      case 'tutor': return t.tutorName || '';
      case 'duracion': return t.durationHours != null ? t.durationHours : -1;
      default: return '';
    }
  };

  const sortedFiltered = !sortField ? filtered : [...filtered].sort((a, b) => {
    const valueA = getSortValue(a, sortField);
    const valueB = getSortValue(b, sortField);
    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
    }
    const strA = valueA.toString().toLowerCase();
    const strB = valueB.toString().toLowerCase();
    if (strA < strB) return sortDirection === 'asc' ? -1 : 1;
    if (strA > strB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SchoolIcon sx={{ color: '#0288d1', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">Capacitaciones</Typography>
            <Typography variant="body2" color="text.secondary">
              Gestión de capacitaciones vinculadas a órdenes de trabajo
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenNew}
          sx={{ bgcolor: '#0288d1', '&:hover': { bgcolor: '#0277bd' }, borderRadius: 2 }}
        >
          Nueva Capacitación
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper sx={{ p: 2, textAlign: 'center', borderLeft: '4px solid #0288d1' }}>
            <Typography variant="h4" fontWeight="bold" color="#0288d1">{trainings.length}</Typography>
            <Typography variant="body2" color="text.secondary">Total capacitaciones</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper sx={{ p: 2, textAlign: 'center', borderLeft: '4px solid #43a047' }}>
            <Typography variant="h4" fontWeight="bold" color="#43a047">
              {trainings.filter(t => t.trainingDate && !isPastTrainingDate(t.trainingDate)).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">Próximas</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Paper sx={{ p: 2, textAlign: 'center', borderLeft: '4px solid #7b1fa2' }}>
            <Typography variant="h4" fontWeight="bold" color="#7b1fa2">
              {trainings.filter(t => isPastTrainingDate(t.trainingDate)).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">Realizadas</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filter */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar por OT, empresa, tema o tutor..."
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          sx={{ width: 350 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9e9e9e' }} /></InputAdornment>
          }}
        />
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortField === 'orden'}
                  direction={sortField === 'orden' ? sortDirection : 'asc'}
                  onClick={() => handleSortRequest('orden')}
                >
                  Orden
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortField === 'empresa'}
                  direction={sortField === 'empresa' ? sortDirection : 'asc'}
                  onClick={() => handleSortRequest('empresa')}
                >
                  Nombre Comercial
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Ente</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortField === 'tema'}
                  direction={sortField === 'tema' ? sortDirection : 'asc'}
                  onClick={() => handleSortRequest('tema')}
                >
                  Tema
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortField === 'tutor'}
                  direction={sortField === 'tutor' ? sortDirection : 'asc'}
                  onClick={() => handleSortRequest('tutor')}
                >
                  Tutor
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortField === 'duracion'}
                  direction={sortField === 'duracion' ? sortDirection : 'asc'}
                  onClick={() => handleSortRequest('duracion')}
                >
                  Duración
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Participantes</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Constancias</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : sortedFiltered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {filterText ? 'No se encontraron resultados' : 'No hay capacitaciones registradas'}
                </TableCell>
              </TableRow>
            ) : sortedFiltered.map(t => {
              const wo = workOrdersMap[t.workOrderId];
              const isPast = isPastTrainingDate(t.trainingDate);
              const entity = getEntityName(t);
              return (
                <TableRow key={t.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color="#0288d1">
                      {wo?.orderNumber || `OT-${t.workOrderId}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{getCompanyName(t) || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {entity.isMatriz
                        ? <BusinessIcon sx={{ fontSize: 16, color: '#0288d1' }} />
                        : <StoreIcon sx={{ fontSize: 16, color: '#7b1fa2' }} />}
                      <Typography variant="body2">{entity.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.trainingTopic}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarTodayIcon sx={{ fontSize: 14, color: '#9e9e9e' }} />
                      <Typography variant="body2">{formatDate(t.trainingDate)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 14, color: '#9e9e9e' }} />
                      <Typography variant="body2">{t.tutorName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {t.durationHours ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: '#9e9e9e' }} />
                        <Typography variant="body2">{t.durationHours} hrs</Typography>
                      </Box>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={isPast ? 'Realizada' : 'Programada'}
                      size="small"
                      sx={{
                        bgcolor: isPast ? '#e8f5e9' : '#e3f2fd',
                        color: isPast ? '#2e7d32' : '#0277bd',
                        fontWeight: 600
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const status = trainingStatusMap[t.id];
                      if (!status) return <Typography variant="caption" color="text.secondary">—</Typography>;
                      if (status.singleProgram) {
                        return (
                          <Tooltip title="Único programa de este ente: todo su padrón lo recibe automáticamente">
                            <Chip label={`Todos (${status.rosterSize})`} size="small" sx={{ bgcolor: '#ede7f6', color: '#4527a0', fontWeight: 600 }} />
                          </Tooltip>
                        );
                      }
                      return (
                        <Chip
                          label={`${status.participantCount}/${status.rosterSize} inscritos`}
                          size="small"
                          sx={{
                            bgcolor: status.participantCount > 0 ? '#e3f2fd' : '#f5f5f5',
                            color: status.participantCount > 0 ? '#0277bd' : '#9e9e9e',
                            fontWeight: 600
                          }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const status = trainingStatusMap[t.id];
                      if (!status) return <Typography variant="caption" color="text.secondary">—</Typography>;
                      if (status.participantCount === 0) {
                        return <Typography variant="caption" color="text.secondary">Sin participantes</Typography>;
                      }
                      return (
                        <Chip
                          label={`${status.generatedCount}/${status.participantCount}`}
                          size="small"
                          sx={{
                            bgcolor: status.ready ? '#e8f5e9' : status.generatedCount > 0 ? '#fff3e0' : '#ffebee',
                            color: status.ready ? '#2e7d32' : status.generatedCount > 0 ? '#e65100' : '#c62828',
                            fontWeight: 700
                          }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Editar capacitación">
                        <IconButton size="small" onClick={() => handleOpenEdit(t)} sx={{ color: '#0288d1' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Participantes inscritos">
                        <IconButton size="small" onClick={() => handleOpenParticipants(t)} sx={{ color: '#43a047' }}>
                          <PeopleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Generar constancias">
                        <IconButton size="small" onClick={() => setConfirmGenerateTraining(t)} sx={{ color: '#7b1fa2' }}>
                          <AssignmentIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog: Nueva / Editar Capacitación */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #0288d1 0%, #0277bd 100%)',
          color: '#fff',
          fontWeight: 700,
          fontSize: '1.4rem',
          py: 3,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5
        }}>
          <SchoolIcon sx={{ fontSize: 30 }} />
          {selectedTraining ? 'Editar Capacitación' : 'Nueva Capacitación'}
        </DialogTitle>

        <DialogContent sx={{ p: 4, background: '#fff' }}>

          {/* Sección: Orden de Trabajo (solo al crear) */}
          {!selectedTraining && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{
                mb: 2, color: '#2c3e50', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 1,
                pb: 1, borderBottom: '2px solid #e3f2fd'
              }}>
                <AssignmentIcon sx={{ color: '#0288d1', fontSize: 20 }} />
                Orden de Trabajo
              </Typography>
              <FormControl fullWidth variant="outlined" required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&:hover fieldset': { borderColor: '#0288d1' }, '&.Mui-focused fieldset': { borderColor: '#0288d1' } } }}
              >
                <InputLabel>Orden de Trabajo *</InputLabel>
                <Select
                  value={form.workOrderId}
                  label="Orden de Trabajo *"
                  onChange={e => {
                    const woId = e.target.value;
                    setForm(p => ({ ...p, workOrderId: woId, branchId: MATRIZ_VALUE }));
                    loadEntitiesForWorkOrder(woId);
                    fetchDefaultTrainingOptions(woId);
                  }}
                >
                  {workOrders.map(wo => (
                    <MenuItem key={wo.id} value={wo.id}>
                      <Box sx={{ py: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                          <Box sx={{
                            bgcolor: '#e3f2fd', color: '#0288d1',
                            fontWeight: 700, fontSize: '0.72rem',
                            px: 1, py: 0.2, borderRadius: 1, whiteSpace: 'nowrap'
                          }}>
                            {wo.orderNumber}
                          </Box>
                        </Box>
                        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                          {companiesMap[wo.companyId]?.businessName || wo.clientName || '—'}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Sección: Ente (Matriz / Sucursal) — fecha, hora y ubicación son independientes por ente */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{
              mb: 2, color: '#2c3e50', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 1,
              pb: 1, borderBottom: '2px solid #e3f2fd'
            }}>
              <BusinessIcon sx={{ color: '#0288d1', fontSize: 20 }} />
              Ente (Matriz / Sucursal)
            </Typography>
            <FormControl fullWidth variant="outlined" required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&:hover fieldset': { borderColor: '#0288d1' }, '&.Mui-focused fieldset': { borderColor: '#0288d1' } } }}
            >
              <InputLabel>Ente *</InputLabel>
              <Select
                value={form.branchId}
                label="Ente *"
                disabled={!!selectedTraining || !form.workOrderId || entitiesLoading}
                onChange={e => setForm(p => ({ ...p, branchId: e.target.value }))}
              >
                {getEntityOptions().map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {opt.isMatriz ? <BusinessIcon sx={{ fontSize: 18, color: '#0288d1' }} /> : <StoreIcon sx={{ fontSize: 18, color: '#7b1fa2' }} />}
                      {opt.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Cada ente puede tener su propia fecha, horario y capacitación dentro de la misma orden.
            </Typography>
          </Box>

          {/* Sección: Información de la Capacitación */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{
              mb: 2, color: '#2c3e50', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 1,
              pb: 1, borderBottom: '2px solid #e3f2fd'
            }}>
              <TopicIcon sx={{ color: '#0288d1', fontSize: 20 }} />
              Información de la Capacitación
            </Typography>
            {!selectedTraining && defaultTrainingOptions.length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">Plantillas del servicio:</Typography>
                {defaultTrainingOptions.map(opt => (
                  <Chip
                    key={opt.id}
                    label={opt.durationHours != null ? `${opt.trainingTopic} (${opt.durationHours}h)` : opt.trainingTopic}
                    size="small"
                    onClick={() => handleApplyDefaultTraining(opt)}
                    sx={{ cursor: 'pointer', bgcolor: '#e3f2fd', color: '#0288d1', fontWeight: 600 }}
                  />
                ))}
              </Box>
            )}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth label="Tema de la Capacitación *"
                  value={form.trainingTopic}
                  onChange={e => setForm(p => ({ ...p, trainingTopic: e.target.value }))}
                  placeholder="Ej: Uso de extintores y evacuación"
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&:hover fieldset': { borderColor: '#0288d1' }, '&.Mui-focused fieldset': { borderColor: '#0288d1' } } }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth label="Fecha de la Capacitación *" type="date"
                  value={form.trainingDate}
                  onChange={e => setForm(p => ({ ...p, trainingDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&:hover fieldset': { borderColor: '#0288d1' }, '&.Mui-focused fieldset': { borderColor: '#0288d1' } } }}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 2 }}>
                <TextField
                  fullWidth label="Hora inicio *" type="time"
                  value={form.startTime}
                  onChange={e => setForm(p => ({ ...p, startTime: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&:hover fieldset': { borderColor: '#0288d1' }, '&.Mui-focused fieldset': { borderColor: '#0288d1' } } }}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 2 }}>
                <TextField
                  fullWidth label="Hora fin *" type="time"
                  value={form.endTime}
                  onChange={e => setForm(p => ({ ...p, endTime: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&:hover fieldset': { borderColor: '#0288d1' }, '&.Mui-focused fieldset': { borderColor: '#0288d1' } } }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth label="Duración (horas)" type="number"
                  value={form.durationHours}
                  onChange={e => setForm(p => ({ ...p, durationHours: e.target.value }))}
                  inputProps={{ min: 0.5, step: 0.5 }}
                  placeholder="Ej: 8"
                  helperText="Duración total de la sesión"
                  variant="outlined"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&:hover fieldset': { borderColor: '#0288d1' }, '&.Mui-focused fieldset': { borderColor: '#0288d1' } } }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Sección: Instructor */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{
              mb: 2, color: '#2c3e50', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 1,
              pb: 1, borderBottom: '2px solid #e3f2fd'
            }}>
              <PersonIcon sx={{ color: '#0288d1', fontSize: 20 }} />
              Instructor
            </Typography>
            <TextField
              fullWidth label="Tutor / Instructor *"
              value={form.tutorName}
              onChange={e => setForm(p => ({ ...p, tutorName: e.target.value }))}
              placeholder="Nombre completo del instructor"
              helperText="Nombre que aparecerá en la constancia"
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&:hover fieldset': { borderColor: '#0288d1' }, '&.Mui-focused fieldset': { borderColor: '#0288d1' } } }}
            />
          </Box>

          {/* Sección: Notas */}
          <Box>
            <Typography variant="h6" sx={{
              mb: 2, color: '#2c3e50', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 1,
              pb: 1, borderBottom: '2px solid #e3f2fd'
            }}>
              📝 Notas Adicionales
            </Typography>
            <TextField
              fullWidth label="Notas" multiline rows={3}
              value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="Observaciones, materiales usados, etc."
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, '&:hover fieldset': { borderColor: '#0288d1' }, '&.Mui-focused fieldset': { borderColor: '#0288d1' } } }}
            />
          </Box>

        </DialogContent>

        <DialogActions sx={{
          p: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          borderTop: '1px solid #e0e0e0'
        }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{
              fontWeight: 600, textTransform: 'none', px: 4, py: 1.5, borderRadius: 3,
              color: '#666', border: '2px solid #ddd',
              '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#bbb' }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={dialogLoading}
            startIcon={dialogLoading ? <CircularProgress size={18} /> : <SaveIcon />}
            sx={{
              fontWeight: 600, textTransform: 'none', px: 4, py: 1.5, borderRadius: 3,
              background: 'linear-gradient(135deg, #0288d1 0%, #0277bd 100%)',
              boxShadow: '0 4px 15px rgba(2,136,209,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0277bd 0%, #01579b 100%)',
                boxShadow: '0 6px 20px rgba(2,136,209,0.5)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {selectedTraining ? 'Guardar Cambios' : 'Crear Capacitación'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Participantes inscritos en el programa */}
      <Dialog
        open={participantsDialogOpen}
        onClose={() => setParticipantsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
          color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5
        }}>
          <PeopleIcon />
          Participantes de "{participantsTraining?.trainingTopic}"
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <DialogContentText sx={{ mb: 2, fontSize: '0.85rem' }}>
            Marca quién del padrón UIPC de este ente asistió a este programa. Si el ente tiene un
            solo programa de capacitación, no hace falta inscribir a nadie aquí -- todos lo reciben
            automáticamente.
          </DialogContentText>
          {participantsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : participantsRoster.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              Este ente no tiene miembros UIPC registrados.
            </Typography>
          ) : (
            participantsRoster.map(member => (
              <FormControlLabel
                key={member.id}
                sx={{ display: 'flex', width: '100%', m: 0, py: 0.3 }}
                control={
                  <Checkbox
                    checked={participantsEnrolledIds.has(member.id)}
                    onChange={() => handleToggleParticipant(member.id)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{member.workerName}</Typography>
                    <Typography variant="caption" color="text.secondary">{member.brigadePosition}</Typography>
                  </Box>
                }
              />
            ))
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setParticipantsDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveParticipants}
            variant="contained"
            disabled={participantsSaving || participantsLoading}
            startIcon={participantsSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ textTransform: 'none', bgcolor: '#43a047', '&:hover': { bgcolor: '#2e7d32' } }}
          >
            Guardar Inscripción
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación previa a "Generar Constancias" -- mismo comportamiento que Programas.jsx:
          mostrar qué se va a generar antes de ejecutar la generación real. */}
      <Dialog
        open={!!confirmGenerateTraining}
        onClose={generatingConstancias ? undefined : () => setConfirmGenerateTraining(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon sx={{ color: '#7b1fa2' }} />
          Generar Constancias
        </DialogTitle>
        <DialogContent dividers>
          {confirmGenerateTraining && (() => {
            const entity = getEntityName(confirmGenerateTraining);
            const status = trainingStatusMap[confirmGenerateTraining.id];
            const participantCount = status?.participantCount ?? 0;
            return (
              <>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Se generarán constancias para:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.75 }}>
                  {participantCount > 0
                    ? <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20, mt: 0.2 }} />
                    : <WarningIcon sx={{ color: '#ed6c02', fontSize: 20, mt: 0.2 }} />}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {entity.name} — {participantCount} integrante{participantCount === 1 ? '' : 's'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {confirmGenerateTraining.trainingTopic}
                    </Typography>
                    {participantCount === 0 && (
                      <Typography variant="caption" color="#ed6c02" sx={{ display: 'block' }}>
                        Sin participantes inscritos
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                  <FolderIcon sx={{ color: '#757575', fontSize: 18, mt: 0.2 }} />
                  <Typography variant="caption" color="text.secondary">
                    Podrás consultar las constancias generadas en Servicios &gt; Gestión Documental.
                  </Typography>
                </Box>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmGenerateTraining(null)} disabled={generatingConstancias}>
            Cancelar
          </Button>
          <Button
            onClick={() => handleGenerateConstancias(confirmGenerateTraining)}
            variant="contained"
            disabled={generatingConstancias || !(trainingStatusMap[confirmGenerateTraining?.id]?.participantCount)}
            sx={{ bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#6a1b9a' }, borderRadius: 2 }}
          >
            {generatingConstancias ? 'Generando...' : 'Confirmar y generar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Capacitaciones;
